import { computed, ref, reactive } from 'vue';
import { defineStore } from 'pinia';
import { useStudyStore } from './study';
import StudyService from '@/services/studyService';
import StudySettingsService from '@/services/studySettingsService';
import { StudySettingsModel, WebhookModel, BillingModel, type StudySettingsUpdateData } from '@/models/studySettings';
import { LoadingStateModel } from '@/models/loadingState';
import { LocalStorageUtils } from '@/utils';

export const useStudySettingsStore = defineStore('studySettings', () => {

  const studyStore = useStudyStore();

  const loaders = {
    initialLoad: reactive(new LoadingStateModel({ isLoading: true })),
    investigation: reactive(new LoadingStateModel({ isLoading: false })),
    billing: reactive(new LoadingStateModel({ isLoading: false })),
    webhooks: reactive(new LoadingStateModel({ isLoading: false })),
    avatar: reactive(new LoadingStateModel({ isLoading: false })),
    archiveStudy: reactive(new LoadingStateModel({ isLoading: false })),
    archiveStudyDialog: reactive(new LoadingStateModel({ isLoading: false })),
    apiKey: reactive(new LoadingStateModel({ isLoading: false }))
  };

  const investigation = ref<StudySettingsModel>(new StudySettingsModel());
  const billing = ref<BillingModel>();
  const webhooks = ref<WebhookModel>(new WebhookModel());
  const studyName = ref<string>('');
  const caseManagement = ref<string>('cross');
  const avatar = ref<string>('');
  const apiKeyExpirationInMonths = ref<number>(0);
  const archive = ref<string>('Active');

  const getInvestigation = async () => {
    loaders.investigation.startLoading();
    try {
      if (studyStore.selectedStudyId) {
        const response = await StudyService.getInvestigation(
          studyStore.selectedStudyId
        ) as StudySettingsModel;
        if (response.archivedAt) {
          archive.value = 'Archive';
        }
        if (response.longitudinal) {
          caseManagement.value = 'long';
        }
        investigation.value = response;
      }
    } catch (error) {
      loaders.investigation.setError(error);
    } finally {
      loaders.investigation.stopLoadingAndFinish();
    }
  };

  const getBilling = async () => {
    if (studyStore.selectedStudyId) {
      loaders.billing.startLoading();

      try {
        const response = await StudyService.getBilling(
          studyStore.selectedStudyId
        ) as BillingModel;
        billing.value = response;
      } catch (error) {
        loaders.billing.setError(error);
      } finally {
        loaders.billing.stopLoadingAndFinish();
      }
    }
  };

  const getUserAvatar = async (avatarURL: string) => {
    loaders.avatar.startLoading();
    try {
      const response = await StudySettingsService.getUserAvatar(
        avatarURL
      ) as string;
      avatar.value = response;
    } catch (error) {
      loaders.avatar.setError(error);
    } finally {
      loaders.avatar.stopLoadingAndFinish();
    }
  };
  const getWebhooks = async (identifier: string) => {
    loaders.webhooks.startLoading();
    try {
      const response = await StudyService.getWebhooks(
        identifier
      ) as WebhookModel;
      // run through the model so newer/missing keys get defaults (also resets
      // to defaults when the response is an empty object, e.g. no webhooks set)
      webhooks.value = new WebhookModel(response);
    } catch (error) {
      loaders.webhooks.setError(error);
    } finally {
      loaders.webhooks.stopLoadingAndFinish();
    }
  };
  const getAllData = async () => {
    loaders.initialLoad.startLoading();
    try {
      await getInvestigation();
      await getBilling();
      await getUserAvatar(investigation.value.avatar);
      if (studyStore.selectedStudyId) {
        await getWebhooks(studyStore.selectedStudyId);
      }
    } catch (error) {
      loaders.initialLoad.setError(error);
    } finally {
      loaders.initialLoad.stopLoadingAndFinish();
      loaders.billing.stopLoadingAndFinish();
      loaders.avatar.stopLoadingAndFinish();
      loaders.archiveStudy.stopLoadingAndFinish();
    }
  };
  const patchDetails = async (details: StudySettingsUpdateData, loader: LoadingStateModel) => {
    if (loader) {
      loader.startLoading();
    }
    await StudyService.patchDetails({
      identifier: investigation.value.identifier,
      ...details
    });
    //update the chaged study name in the cache
    studyStore.selectedStudy!.title = details.title ?? '';
    studyStore.selectedStudy!.identifier = investigation.value.identifier;
    LocalStorageUtils.setStudyDetails(studyStore.selectedStudy!);
    studyStore.getStudies();
    if (loader) {
      loader.stopLoadingAndFinish();
    }
  };
  const patchApiKey = async () => {
    loaders.apiKey.startLoading();
    const response = await StudyService.patchApiKey(
      investigation.value.identifier,
      apiKeyExpirationInMonths.value
    );
    investigation.value.apiKey = response.apiKey;
    investigation.value.apiKeyExpiration = response.apiKeyExpiration;
    loaders.apiKey.stopLoadingAndFinish();
  };

  const isInitialLoadLoading = computed(() => loaders.initialLoad.isLoading);
  const isInvestigationLoading = computed(() => loaders.investigation.isLoading);
  const isWebhooksLoading = computed(() => loaders.webhooks.isLoading);
  const isAvatarLoading = computed(() => loaders.avatar.isLoading);
  const isArchiveStudyLoading = computed(() => loaders.archiveStudy.isLoading);
  const isArchiveStudyDialogLoading = computed(() => loaders.archiveStudyDialog.isLoading);
  const isApiKeyLoading = computed(() => loaders.apiKey.isLoading);


  const studyFlagsLoader = ref(new LoadingStateModel({ isLoading: false }));
  const updateStudyFlags = async () => {
    studyFlagsLoader.value.startLoading();
    try {
      await StudyService.updateFlags(studyStore.selectedStudy!.identifier, studyStore.selectedStudy!.flags!);
      studyFlagsLoader.value.setSuccess('Study preferences updated!');
      studyStore.getStudies();
    } catch (error) {
      studyFlagsLoader.value.setError(error);
    } finally {
      studyFlagsLoader.value.stopLoadingAndFinish();
    }
  };


  return {
    getInvestigation,
    getBilling,
    getUserAvatar,
    getAllData,
    patchDetails,
    patchApiKey,
    updateStudyFlags,
    isInitialLoadLoading,
    isInvestigationLoading,
    isWebhooksLoading,
    isAvatarLoading,
    isArchiveStudyLoading,
    isArchiveStudyDialogLoading,
    isApiKeyLoading,
    loaders,
    investigation,
    billing,
    webhooks,
    studyName,
    caseManagement,
    avatar,
    apiKeyExpirationInMonths,
    archive
  };
});
